/**
 *  Copyright (c) 2021-present The TVM Engineering Authors.
 *
 */

import i18n from "../../services/i18n";

export const textLabelTable = {
  body: {
    noMatch: i18n.t("core:noMatch"),
    toolTip: i18n.t("core:toolTipSort"),
    columnHeaderTooltip: (column) => i18n.t("core:columnHeaderTooltip") + ` ${column.label}`,
  },
  pagination: {
    next: i18n.t("core:nextPage"),
    previous: i18n.t("core:previousPage"),
    rowsPerPage: i18n.t("core:rowsPerPage"),
    displayRows: i18n.t("core:displayRows"),
  },
  toolbar: {
    search: i18n.t("core:search"),
    downloadCsv: i18n.t("core:downloadCsv"),
    print: i18n.t("core:printTable"),
    viewColumns: i18n.t("core:viewColumns"),
    filterTable: i18n.t("core:filterTable"),
  },
  filter: {
    all: i18n.t("core:all"),
    title: i18n.t("core:titleFilters"),
    reset: i18n.t("core:resetTable"),
  },
  viewColumns: {
    title: i18n.t("core:titleTable"),
    titleAria: i18n.t("core:titleAria"),
  },
  selectedRows: {
    text: i18n.t("core:selectedRowsText"),
    delete: i18n.t("core:deleteRows"),
    deleteAria: i18n.t("core:deleteAria"),
  },
};

export const options = {
  textLabels: textLabelTable,
  filter: true,
  sort: true,
  filterType: "dropdown",
  resizableColumns: false,
  responsive: "stacked",
  rowsPerPage: 5,
  rowsPerPageOptions: [5, 10, 25, 50],
  downloadOptions: {
    filename: "excel-format.csv",
    separator: ";",
    filterOptions: {
      useDisplayedColumnsOnly: true,
      useDisplayedRowsOnly: true,
    },
  },
};

export const userColumn = (
  editUser,
  projectsTableLink,
  ActiveButton,
  renderStatus,
  displayInitialProject,
  RoleChangeDialogDisplay
) => {
  return [
    {
      name: "uuid",
      label: i18n.t("core:userID"),
      options: { filter: true, sort: true, display: false },
    },
    {
      name: "user",
      label: i18n.t("core:user"),
      options: { filter: true, sort: true, customBodyRender: editUser },
    },
    { name: "email", label: i18n.t("core:email"), options: { filter: true, sort: true } },
    { name: "firstName", label: i18n.t("core:firstName"), options: { filter: true, sort: true } },
    { name: "lastName", label: i18n.t("core:lastName"), options: { filter: true, sort: true } },
    {
      name: "projects",
      label: i18n.t("core:projects"),
      options: { filter: true, sort: true, customBodyRender: projectsTableLink },
    },
    {
      name: "tools",
      label: i18n.t("core:tools"),
      options: { filter: true, sort: true, display: false },
    },
    {
      name: "status",
      label: i18n.t("core:status"),
      options: { filter: true, sort: true, customBodyRender: renderStatus },
    },
    {
      name: "activated",
      label: i18n.t("core:active"),
      options: { filter: true, sort: true, display: true, customBodyRender: ActiveButton },
    },
    {
      name: "iniproject",
      label: "Initial Project",
      options: { filter: true, sort: true, display: true, customBodyRender: displayInitialProject },
    },
    { name: "company", label: i18n.t("core:company"), options: { filter: true, sort: true } },
    {
      name: "companyURL",
      label: i18n.t("core:companyUrl"),
      options: { filter: true, sort: true, display: false },
    },
    {
      name: "businessRole",
      label: i18n.t("core:businessRole"),
      options: { filter: true, sort: true },
    },
    {
      name: "phone",
      label: i18n.t("core:phone"),
      options: { filter: true, sort: true, display: false },
    },
    {
      name: "zipCode",
      label: i18n.t("core:zipCode"),
      options: { filter: true, sort: true, display: false },
    },
    {
      name: "industry",
      label: i18n.t("core:industry"),
      options: { filter: true, sort: true, display: false },
    },
    {
      name: "country",
      label: i18n.t("core:country"),
      options: { filter: true, sort: true, display: false },
    },
    {
      name: "city",
      label: i18n.t("core:city"),
      options: { filter: true, sort: true, display: false },
    },
    {
      name: "role",
      label: i18n.t("core:role"),
      options: {
        filter: true,
        sort: true,
        display: true,
        customBodyRender: RoleChangeDialogDisplay,
      },
    },
    { name: "created", label: i18n.t("core:created"), options: { filter: true, sort: true } },
  ];
};

export const projectsColumn = (EditButton) => {
  return [
    { name: "id", label: i18n.t("core:id"), options: { filter: true, sort: true, display: false } },
    { name: "name", label: i18n.t("core:nameColumn"), options: { filter: true, sort: true } },
    {
      name: "description",
      label: i18n.t("core:description"),
      options: { filter: true, sort: true },
    },
    { name: "created", label: i18n.t("core:created"), options: { filter: true, sort: true } },
    {
      name: "edit",
      label: i18n.t("core:edit"),
      options: { filter: true, sort: true, display: true, customBodyRender: EditButton },
    },
  ];
};

export const DeviceColumns = () => {
  return [
    { name: "id", label: i18n.t("core:id"), options: { filter: true, sort: true, display: false } },
    { name: "project", label: i18n.t("core:project"), options: { filter: true, sort: true } },
    {
      name: "description",
      label: i18n.t("core:description"),
      options: { filter: true, sort: true },
    },
    { name: "powerClass", label: i18n.t("core:powerClass"), options: { filter: true, sort: true } },
    { name: "typeOfApp", label: i18n.t("core:typeOfApp"), options: { filter: true, sort: true } },
    {
      name: "marketSelect",
      label: i18n.t("core:marketSelect"),
      options: { filter: true, sort: true },
    },
    { name: "motorType", label: i18n.t("core:motorType"), options: { filter: true, sort: true } },
    { name: "Created", label: i18n.t("core:created"), options: { filter: true, sort: true } },
    { name: "", label: "", options: { filter: true, sort: true } },
  ];
};

export const ToolsColumns = (downloadLink) => {
  return [
    { name: "id", label: i18n.t("core:id"), options: { filter: true, sort: true, display: false } },
    {
      name: "tool_name",
      label: i18n.t("core:toolsServices"),
      options: { filter: true, sort: true, customBodyRender: downloadLink },
    },
    {
      name: "description",
      label: i18n.t("core:description"),
      options: { filter: true, sort: true },
    },
    { name: "status", label: i18n.t("core:status"), options: { filter: true, sort: true } },
    { name: "serial_id", label: i18n.t("core:serialId"), options: { filter: true, sort: true } },
    { name: "time_to_use", label: i18n.t("core:timeToUse"), options: { filter: true, sort: true } },
  ];
};

export const TermsAndConditionsColumns = (/* getDataRowByCLick */) => {
  return [
    { name: "id", label: i18n.t("core:id"), options: { filter: true, sort: true, display: false } },
    {
      name: "terms_name",
      label: i18n.t("core:termsName"),
      options: { filter: true, sort: true /* , customBodyRender: getDataRowByCLick */ },
    },
    {
      name: "description",
      label: i18n.t("core:description"),
      options: { filter: true, sort: true },
    },
    // { name: 'terms_html', label: i18n.t('core:termsContent'), options: { filter: true, sort: true } },
    // { name: 'upload_path', label: i18n.t('core:file'), options: { filter: true, sort: true } },
    { name: "created", label: i18n.t("core:created"), options: { filter: true, sort: true } },
    { name: "", label: "", options: { filter: true, sort: true } },
  ];
};
export const TermsAndConditionsColumnsVesions = (getDataRowByCLick) => {
  return [
    { name: "id", label: i18n.t("core:id"), options: { filter: true, sort: true, display: false } },
    {
      name: "N",
      label: "Number",
      options: { filter: true, sort: true, customBodyRender: getDataRowByCLick },
    },
    {
      name: "terms_name",
      label: i18n.t("core:termsName"),
      options: { filter: true, sort: true },
    },
    {
      name: "description",
      label: i18n.t("core:description"),
      options: { filter: true, sort: true },
    },
    // { name: 'terms_html', label: i18n.t('core:termsContent'), options: { filter: true, sort: true } },
    // { name: 'upload_path', label: i18n.t('core:file'), options: { filter: true, sort: true } },
    { name: "created", label: i18n.t("core:created"), options: { filter: true, sort: true } },
  ];
};

export const UploadsColumns = (downloadLink) => {
  return [
    { name: "id", label: i18n.t("core:id"), options: { filter: true, sort: true, display: false } },
    {
      name: "ressource",
      label: i18n.t("core:ressourceName"),
      options: { filter: true, sort: true, customBodyRender: downloadLink },
    },
    {
      name: "description",
      label: i18n.t("core:description"),
      options: { filter: true, sort: true },
    },
    {
      name: "upload_path",
      label: i18n.t("core:file"),
      options: { filter: true, sort: true, display: false },
    },
    {
      name: "created",
      label: i18n.t("core:created"),
      options: { filter: true, sort: true, display: false },
    },
  ];
};

export const ToolsTableInUploadsColumns = (openDrawer, downloadButton) => {
  return [
    {
      name: "",
      // label: "",
      options: { filter: true, sort: true, customBodyRender: downloadButton },
    },
    { name: "id", label: i18n.t("core:id"), options: { filter: true, sort: true, display: false } },
    {
      name: "tool_name",
      label: "Filename",
      options: { filter: true, sort: true, display: true },
    },

    {
      name: "serial_id",
      label: "Serial ID",
      options: { filter: true, sort: true, display: true },
    },
    {
      name: "terms",
      label: "Terms",
      options: { filter: true, sort: true, display: true },
    },
    // {
    //   name: "created",
    //   label: i18n.t("core:created"),
    //   options: { filter: true, sort: true, display: false },
    // },
    // {
    //   name: "description",
    //   label: i18n.t("core:description"),
    //   options: { filter: true, sort: true },
    // },
    {
      name: "upload_path2",
      label: i18n.t("core:file"),
      options: { filter: true, sort: true },
    },
    {
      name: "upload_path",
      label: "upload path",
      options: { filter: true, sort: true, display: false },
    },
    {
      name: "",
      label: "",
      options: { filter: true, sort: true, customBodyRender: openDrawer },
    },
  ];
};

export const DownloadHistoryColumns = () => {
  return [
    { name: "id", label: i18n.t("core:id"), options: { filter: true, sort: true, display: false } },
    { name: "username", label: "Username", options: { filter: true, sort: true, display: true } },
    { name: "email", label: "Email", options: { filter: true, sort: true, display: true } },
    { name: "name", label: "Name", options: { filter: true, sort: true, display: true } },
    { name: "role", label: "Role", options: { filter: true, sort: true, display: true } },
    {
      name: "project_name",
      label: "Project",
      options: { filter: true, sort: true, display: true },
    },
    { name: "tool_name", label: "Tool Name", options: { filter: true, sort: true, display: true } },
    { name: "terms", label: "File", options: { filter: true, sort: true, display: true } },
    {
      name: "download_at",
      label: "Downloaded On",
      options: { filter: true, sort: true, display: true },
    },

    // {
    //   name: "ressource",
    //   label: i18n.t("core:ressourceName"),
    //   options: { filter: true, sort: true, customBodyRender: downloadLink },
    // },
    // {
    //   name: "description",
    //   label: i18n.t("core:description"),
    //   options: { filter: true, sort: true },
    // },
    // {
    //   name: "upload_path",
    //   label: i18n.t("core:file"),
    //   options: { filter: true, sort: true, display: false },
    // },
    // {
    //   name: "created",
    //   label: i18n.t("core:created"),
    //   options: { filter: true, sort: true, display: false },
    // },
  ];
};
